import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import api from 'api';
import useFeedback from 'hooks/useFeedback';
import useUser from 'hooks/useUser';
import { verificationRoutes } from 'helpers/routes';
import { USER_TYPES } from 'helpers/roles';

import {
  getApplication,
  updateApplicationData,
} from 'store/actions/applications';

const useCurrentApplication = () => {
  const dispatch = useDispatch();
  const { appId, role } = useParams();
  const { setSuccess, setError } = useFeedback();
  const { isSiteReviewer } = useUser();

  const appGet = React.useCallback(() => {
    dispatch(getApplication(appId));
  }, [dispatch, appId]);

  const appDelete = React.useCallback(
    (cb) => {
      api.deleteApplication(appId).then(cb);
    },
    [appId],
  );

  const appArchive = React.useCallback(
    async ({ reason, cb }) => {
      try {
        await api.archiveApplication(appId, reason);
        appGet();
        setSuccess('Verification archived');
        if (cb) cb();
      } catch (_) {
        setError('Error archiving verification');
      }
    },
    [appGet, appId, setError, setSuccess],
  );

  const appUnarchive = React.useCallback(
    async ({ reason, cb }) => {
      try {
        await api.unarchiveApplication(appId, reason);
        appGet();
        setSuccess('Verification unarchived');
        if (cb) cb();
      } catch (_) {
        setError('Error unarchiving verification');
      }
    },
    [appGet, appId, setError, setSuccess],
  );

  const updateSiteVisitDate = React.useCallback(
    async ({ date }) => {
      const updateData = { siteVisitAt: date };
      try {
        await api.setSiteVisitDate({
          appId,
          ...updateData,
        });
        dispatch(updateApplicationData(updateData));
        setSuccess('Site Visit Date saved');
      } catch (_) {
        setError('Error saving Site Visit Date');
      }
    },
    [appId, dispatch, setSuccess, setError],
  );

  const updateExpirationDate = React.useCallback(
    async ({ date }) => {
      const updateData = { expiredAt: date };
      try {
        await api.setExpirationDate({
          appId,
          ...updateData,
        });
        dispatch(updateApplicationData(updateData));
        setSuccess('Expiration Date saved');
      } catch (_) {
        setError('Error saving Expiration Date');
      }
    },
    [appId, dispatch, setSuccess, setError],
  );

  const { data, isLoading } = useSelector(
    ({ applications }) => applications.currentApplication,
  );

  const isReviewer = React.useMemo(
    () => role === verificationRoutes.REVIWER && isSiteReviewer,
    [role, isSiteReviewer],
  );

  const isVerification = React.useMemo(
    () => role === verificationRoutes.VERIFICATION,
    [role],
  );

  const userTypeId = React.useMemo(() => USER_TYPES[role], [role]);

  return {
    appGet,
    appDelete,
    appArchive,
    appUnarchive,
    updateSiteVisitDate,
    updateExpirationDate,
    isLoading,
    role,
    isReviewer,
    isVerification,
    userTypeId,
    appData: data,
  };
};

export default useCurrentApplication;
